import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import NotificationsBell from '@/components/NotificationsBell';
import { singleAvailabilityResetPayload } from '@/components/myAvailabilityPayloads';
import {
  dedupePublishedShiftsByDate, isPublishedRest, localCalendarDate,
} from '@/components/publishedShiftPresentation';
import {
  CalendarDays, Check, CircleHelp, CircleOff, Clock3, Copy, FilePenLine,
  Loader2, Menu as MenuIcon, RotateCcw, Save, Sparkles, UtensilsCrossed,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSelfEmployee } from '@/lib/selfIdentity';

const MONTHS_IT = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const DAYS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const EMPTY_ENTRY = {
  state: 'NON_INDICATA', start_time: '', end_time: '', notes: '',
};
const SIMULATION_SERVICES = [
  { id: 'simulation-pranzo', code: 'PRANZO', name: 'Pranzo' },
  { id: 'simulation-cena', code: 'CENA', name: 'Cena' },
];

const AVAILABILITY_STATES = [
  {
    value: 'NON_INDICATA', label: 'Non indicata', icon: CircleHelp,
    className: 'border-stone-200 bg-stone-50 text-stone-600',
    activeClass: 'border-stone-500 bg-stone-200 text-stone-900 ring-2 ring-stone-200',
  },
  {
    value: 'DISPONIBILE', label: 'Disponibile', icon: Check,
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    activeClass: 'border-emerald-500 bg-emerald-100 text-emerald-800 ring-2 ring-emerald-200',
  },
  {
    value: 'INDISPONIBILE', label: 'Indisponibile', icon: CircleOff,
    className: 'border-red-200 bg-red-50 text-red-700',
    activeClass: 'border-red-500 bg-red-100 text-red-800 ring-2 ring-red-200',
  },
  {
    value: 'PREFERISCO_LAVORARE', label: 'Preferisco lavorare', icon: Sparkles,
    className: 'border-amber-200 bg-amber-50 text-amber-700',
    activeClass: 'border-amber-500 bg-amber-100 text-amber-800 ring-2 ring-amber-200',
  },
  {
    value: 'DISPONIBILE_SE_SERVE', label: 'Disponibile se serve', icon: UtensilsCrossed,
    className: 'border-blue-200 bg-blue-50 text-blue-700',
    activeClass: 'border-blue-500 bg-blue-100 text-blue-800 ring-2 ring-blue-200',
  },
];

const stateMeta = (state) => AVAILABILITY_STATES.find(item => item.value === state)
  || AVAILABILITY_STATES[0];
const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};
const localDateValue = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);
const localDate = (value) => {
  return localCalendarDate(value);
};
const startOfIsoWeek = (date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - ((result.getDay() + 6) % 7));
  return result;
};
const formatLongDate = (date) => date.toLocaleDateString('it-IT', {
  weekday: 'long', day: 'numeric', month: 'long',
});
const formatShortDate = (date) => `${date.getDate()} ${MONTHS_IT[date.getMonth()]}`;
const formatTime = (value) => (value || '').slice(0, 5);
const keyFor = (date, serviceId) => `${date}::${serviceId}`;
const formatAvailabilityTime = (item) => (
  item.start_time && item.end_time
    ? `${formatTime(item.start_time)}–${formatTime(item.end_time)}`
    : 'Orario non specificato'
);
const entryFromApi = (item) => ({
  state: item.state === 'NON_DISPONIBILE' ? 'INDISPONIBILE'
    : item.state === 'PREFERENZA' ? 'PREFERISCO_LAVORARE' : item.state,
  start_time: formatTime(item.start_time),
  end_time: formatTime(item.end_time),
  notes: item.notes || '',
});
const sameEntry = (a, b) => (
  (a?.state || EMPTY_ENTRY.state) === (b?.state || EMPTY_ENTRY.state)
  && (a?.start_time || '') === (b?.start_time || '')
  && (a?.end_time || '') === (b?.end_time || '')
  && (a?.notes || '') === (b?.notes || '')
);

function daysBetween(start, end) {
  const days = [];
  for (let day = localDate(start); day <= localDate(end); day = addDays(day, 1)) {
    days.push(new Date(day));
  }
  return days;
}

function nextFutureWeek() {
  return startOfIsoWeek(addDays(new Date(), 7));
}

function AvailabilityStateButtons({ value, onChange, serviceCode }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="group" aria-label="Stato disponibilità">
      {AVAILABILITY_STATES.map(option => {
        const Icon = option.icon;
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={selected}
            className={`flex min-h-11 items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green ${
              selected ? option.activeClass : option.className
            }`}
            data-testid={`my-availability-state-${serviceCode.toLowerCase()}-${option.value.toLowerCase()}`}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ServiceAvailabilityForm({ service, entry, onChange, onReset }) {
  const state = entry?.state || EMPTY_ENTRY.state;
  const updateState = (nextState) => onChange({
    state: nextState,
    ...(nextState === 'NON_INDICATA' ? { start_time: '', end_time: '', notes: '' } : {}),
  });
  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-3 shadow-sm" data-testid={`my-availability-service-${service.code.toLowerCase()}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${service.code === 'PRANZO' ? 'bg-amber-400' : 'bg-blue-400'}`} />
          <h4 className="font-display text-base font-semibold text-brand-navy">{service.name}</h4>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Servizio</span>
      </div>
      <AvailabilityStateButtons
        value={state}
        onChange={nextState => (nextState === 'NON_INDICATA' ? onReset() : updateState(nextState))}
        serviceCode={service.code}
      />
      {state !== 'NON_INDICATA' && (
        <div className="mt-3 space-y-3 border-t border-stone-100 pt-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-semibold text-stone-600">
              <span className="mb-1 flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> Dal</span>
              <input
                type="time"
                value={entry?.start_time || ''}
                onChange={event => onChange({ start_time: event.target.value })}
                autoComplete="off"
                className="h-10 w-full rounded-xl border border-stone-200 bg-stone-50 px-2 text-sm text-stone-800 focus:border-brand-green focus:outline-none"
                data-testid={`my-availability-start-${service.code.toLowerCase()}`}
              />
            </label>
            <label className="text-xs font-semibold text-stone-600">
              <span className="mb-1 block">Alle</span>
              <input
                type="time"
                value={entry?.end_time || ''}
                onChange={event => onChange({ end_time: event.target.value })}
                autoComplete="off"
                className="h-10 w-full rounded-xl border border-stone-200 bg-stone-50 px-2 text-sm text-stone-800 focus:border-brand-green focus:outline-none"
                data-testid={`my-availability-end-${service.code.toLowerCase()}`}
              />
            </label>
          </div>
          <label className="block text-xs font-semibold text-stone-600">
            <span className="mb-1 block">Nota (facoltativa)</span>
            <textarea
              value={entry?.notes || ''}
              onChange={event => onChange({ notes: event.target.value })}
              rows={2}
              maxLength={500}
              placeholder="Es. disponibile solo dopo le 19"
              className="w-full resize-none rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-sm font-normal text-stone-800 focus:border-brand-green focus:outline-none"
              data-testid={`my-availability-notes-${service.code.toLowerCase()}`}
            />
          </label>
        </div>
      )}
    </section>
  );
}

function SavedAvailabilityRow({ item, service, readOnly, onEdit, onDelete }) {
  const meta = stateMeta(entryFromApi(item).state);
  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border px-3 py-3 sm:flex-row sm:items-center sm:justify-between ${
        readOnly ? 'border-stone-100 bg-stone-50/70' : 'border-emerald-100 bg-white'
      }`}
      data-testid={`${readOnly ? 'my-availability-history' : 'my-availability-saved'}-${item.shift_date}-${item.service_id || 'all'}`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-semibold capitalize text-brand-navy">{formatLongDate(localDate(item.shift_date))}</div>
          <span className={`rounded-full border px-2 py-1 text-[10px] font-bold ${meta.className}`}>{meta.label}</span>
        </div>
        <div className="mt-1 text-xs text-stone-500">
          {service?.name || 'Tutti i servizi'} · {formatAvailabilityTime(item)}
        </div>
        {item.notes && <div className="mt-1 truncate text-xs text-stone-600">{item.notes}</div>}
      </div>
      {readOnly ? (
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-stone-400">Sola lettura</span>
      ) : (
        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="outline" onClick={() => onEdit(item)}
            className="h-9 rounded-xl border-stone-200 bg-white px-3 text-xs font-semibold" data-testid={`my-availability-edit-${item.shift_date}-${item.service_id || 'all'}`}>
            <FilePenLine className="mr-1.5 h-3.5 w-3.5" /> Modifica
          </Button>
          <Button type="button" variant="outline" onClick={() => onDelete(item)}
            className="h-9 rounded-xl border-red-200 bg-white px-3 text-xs font-semibold text-red-700 hover:bg-red-50" data-testid={`my-availability-delete-${item.shift_date}-${item.service_id || 'all'}`}>
            Elimina
          </Button>
        </div>
      )}
    </div>
  );
}

function DayCard({ date, services, getEntry, onOpen, onCopy }) {
  const dateValue = localDateValue(date);
  const isPast = date <= new Date(new Date().setHours(0, 0, 0, 0));
  return (
    <article className={`rounded-2xl border p-3 ${isPast ? 'border-stone-100 bg-stone-50/70 opacity-60' : 'border-stone-200 bg-white shadow-sm'}`}
      data-testid={`my-availability-day-${dateValue}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-green">{DAYS_IT[(date.getDay() + 6) % 7]}</div>
          <div className="font-display text-lg font-semibold capitalize text-brand-navy">{formatShortDate(date)}</div>
        </div>
        {!isPast && (
          <button type="button" onClick={() => onCopy(date)} title="Copia questo giorno al giorno successivo"
            className="inline-flex h-9 items-center gap-1 rounded-xl border border-stone-200 px-2 text-[11px] font-semibold text-stone-600 hover:bg-stone-50"
            data-testid={`my-availability-copy-day-${dateValue}`}>
            <Copy className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Copia giorno</span>
          </button>
        )}
      </div>
      <div className="mt-3 space-y-2">
        {services.map(service => {
          const entry = getEntry(dateValue, service.id);
          const meta = stateMeta(entry.state);
          return (
           <button type="button" key={service.id} onClick={() => onOpen(date, service.id)} disabled={isPast}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-stone-100 bg-stone-50/70 px-3 py-2 text-left hover:bg-stone-100"
              data-testid={`my-availability-open-${dateValue}-${service.code.toLowerCase()}`}>
              <span className="text-xs font-semibold text-stone-700">{service.name}</span>
              <span className={`max-w-[145px] truncate rounded-full border px-2 py-1 text-[10px] font-bold ${meta.className}`}>
                {meta.label}
              </span>
            </button>
          );
        })}
      </div>
    </article>
  );
}

export default function MyAvailability() {
  const { openMobileMenu = () => {} } = useOutletContext() || {};
  const { isSimulated } = useSelfEmployee();
  const [services, setServices] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [futureAvailability, setFutureAvailability] = useState([]);
  const [historyAvailability, setHistoryAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [savedLoading, setSavedLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [mode, setMode] = useState('WEEK');
  const [weekStart, setWeekStart] = useState(nextFutureWeek);
  const [customStart, setCustomStart] = useState(localDateValue(addDays(new Date(), 1)));
  const [customEnd, setCustomEnd] = useState(localDateValue(addDays(new Date(), 7)));
  const [draft, setDraft] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [pendingReset, setPendingReset] = useState(null);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetCount, setResetCount] = useState(null);
  const [resetCountLoading, setResetCountLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const draftRef = useRef({});

  const futureFrom = localDateValue(addDays(new Date(), 1));
  const today = localDateValue(new Date());
  const shiftsFrom = localDateValue(new Date());
  const shiftsTo = localDateValue(addDays(new Date(), 56));
  const editorStart = mode === 'WEEK' ? localDateValue(weekStart) : customStart;
  const editorEnd = mode === 'WEEK' ? localDateValue(addDays(weekStart, 6)) : customEnd;
  const days = useMemo(() => (
    editorStart && editorEnd && editorEnd >= editorStart ? daysBetween(editorStart, editorEnd) : []
  ), [editorEnd, editorStart]);
  const dirty = Object.keys(draft).length > 0;
  const publishedShiftCards = useMemo(() => dedupePublishedShiftsByDate(shifts), [shifts]);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    if (isSimulated) {
      setServices(SIMULATION_SERVICES);
      setShifts([]);
      setLoading(false);
      return;
    }
    try {
      const [serviceData, shiftData] = await Promise.all([
        apiFetch('/api/turni/services'),
        apiFetch(`/api/turni/my-shifts?from_date=${shiftsFrom}&to_date=${shiftsTo}`),
      ]);
      setServices((serviceData || []).filter(service => ['PRANZO', 'CENA'].includes(service.code)));
      setShifts(Array.isArray(shiftData) ? shiftData : []);
    } catch (error) {
      setLoadError(error.message || 'Non è stato possibile caricare i tuoi turni.');
    } finally {
      setLoading(false);
    }
  }, [isSimulated, shiftsFrom, shiftsTo]);

  const loadAvailability = useCallback(async () => {
    if (isSimulated) {
      setAvailability([]);
      setAvailabilityLoading(false);
      return;
    }
    if (!editorOpen || !editorStart || !editorEnd || editorEnd < editorStart) return;
    setAvailabilityLoading(true);
    try {
      const data = await apiFetch(`/api/turni/my-availability?from_date=${editorStart}&to_date=${editorEnd}`);
      setAvailability(Array.isArray(data) ? data : []);
      if (Object.keys(draftRef.current).length === 0) setDraft({});
    } catch (error) {
      toast.error(error.message || 'Non è stato possibile caricare le disponibilità.');
    } finally {
      setAvailabilityLoading(false);
    }
  }, [editorEnd, editorOpen, editorStart, isSimulated]);

  const loadSavedAvailability = useCallback(async () => {
    if (isSimulated) {
      setFutureAvailability([]);
      setHistoryAvailability([]);
      setSavedLoading(false);
      return;
    }
    setSavedLoading(true);
    try {
      const [futureData, historyData] = await Promise.all([
        apiFetch(`/api/turni/my-availability?from_date=${futureFrom}&to_date=9999-12-31`),
        apiFetch(`/api/turni/my-availability?from_date=2000-01-01&to_date=${today}`),
      ]);
      setFutureAvailability(Array.isArray(futureData) ? futureData : []);
      setHistoryAvailability(Array.isArray(historyData) ? historyData : []);
    } catch (error) {
      toast.error(error.message || 'Non è stato possibile caricare le disponibilità salvate.');
    } finally {
      setSavedLoading(false);
    }
  }, [futureFrom, isSimulated, today]);

  useEffect(() => { loadPage(); }, [loadPage]);
  useEffect(() => {
    const revalidate = () => { if (document.visibilityState === 'visible') loadPage(); };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
    };
  }, [loadPage]);
  useEffect(() => { loadAvailability(); }, [loadAvailability]);
  useEffect(() => { loadSavedAvailability(); }, [loadSavedAvailability]);

  const apiEntry = (date, serviceId) => {
    const sources = [...availability, ...futureAvailability];
    const exact = sources.find(item => item.shift_date === date && item.service_id === serviceId);
    return exact ? entryFromApi(exact) : EMPTY_ENTRY;
  };
  const savedEntry = (date, serviceId) => (
    futureAvailability.find(item => item.shift_date === date && item.service_id === serviceId)
      || availability.find(item => item.shift_date === date && item.service_id === serviceId)
  );
  const getEntry = (date, serviceId) => draft[keyFor(date, serviceId)] || apiEntry(date, serviceId);
  const updateEntry = (date, serviceId, changes) => {
    if (localDate(date) <= localDate(today)) return;
    const key = keyFor(date, serviceId);
    setDraft(current => ({
      ...current,
      [key]: { ...getEntry(date, serviceId), ...changes },
    }));
  };
  const openDay = (date) => {
    setSelectedDay(date);
  };
  const openSavedAvailability = (item) => {
    if (dirty) {
      toast.error('Salva o annulla le modifiche prima di aprire un’altra disponibilità.');
      return;
    }
    const date = localDate(item.shift_date);
    setEditorOpen(true);
    setMode('CUSTOM');
    setCustomStart(item.shift_date);
    setCustomEnd(item.shift_date);
    openDay(date);
  };
  const requestSingleReset = (date, serviceId) => {
    if (!date || !serviceId) {
      toast.error('La disponibilità selezionata non contiene data o servizio.');
      return;
    }
    const existing = savedEntry(date, serviceId);
    if (!existing) {
      updateEntry(date, serviceId, { state: 'NON_INDICATA', start_time: '', end_time: '', notes: '' });
      return;
    }
    setPendingReset({ date, serviceId, item: existing });
  };

  const guardDirty = (action) => {
    if (dirty) {
      toast.error('Salva o annulla le modifiche prima di cambiare periodo.');
      return;
    }
    action();
  };
  const changeWeek = (amount) => guardDirty(() => setWeekStart(current => addDays(current, amount * 7)));
  const changeMode = (nextMode) => guardDirty(() => setMode(nextMode));
  const updateCustomRange = (setter, value) => {
    if (dirty) {
      toast.error('Salva o annulla le modifiche prima di cambiare periodo.');
      return;
    }
    setter(value);
  };

  const copyDay = (sourceDate) => {
    const source = localDateValue(sourceDate);
    const targetDate = addDays(sourceDate, 1);
    const target = localDateValue(targetDate);
    services.forEach(service => updateEntry(target, service.id, { ...getEntry(source, service.id) }));
    if (!days.some(day => localDateValue(day) === target)) {
      setMode('CUSTOM');
      setCustomStart(source);
      setCustomEnd(target);
    }
    toast.success(`Giorno copiato al ${formatLongDate(targetDate)}`);
  };

  const copyWeek = () => {
    const sourceStart = mode === 'WEEK' ? weekStart : startOfIsoWeek(localDate(editorStart));
    const sourceDays = Array.from({ length: 7 }, (_, index) => addDays(sourceStart, index));
    const targetStart = addDays(sourceStart, 7);
    sourceDays.forEach((sourceDate, index) => {
      const target = localDateValue(addDays(targetStart, index));
      services.forEach(service => updateEntry(target, service.id, {
        ...getEntry(localDateValue(sourceDate), service.id),
      }));
    });
    setMode('CUSTOM');
    setCustomStart(localDateValue(targetStart));
    setCustomEnd(localDateValue(addDays(targetStart, 6)));
    toast.success('Settimana copiata. Controlla e salva le modifiche.');
  };

  const saveAvailability = async (force = false) => {
    if (isSimulated) {
      toast.error('La modalità simulazione non può modificare disponibilità reali.');
      return;
    }
    const entries = Object.entries(draft).map(([key, entry]) => {
      const [shiftDate, serviceId] = key.split('::');
      return { shift_date: shiftDate, service_id: serviceId, ...entry };
    });
    if (!entries.length) return;
    const conflicts = entries.filter(entry => !sameEntry(
      entry,
      apiEntry(entry.shift_date, entry.service_id),
    ));
    if (conflicts.length && !force) {
      setPendingSave(true);
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/api/turni/my-availability', {
        method: 'PUT',
        body: JSON.stringify({
          items: entries.map(entry => ({
            shift_date: entry.shift_date,
            service_id: entry.service_id,
            state: entry.state,
            ...(entry.start_time && entry.end_time
              ? { start_time: entry.start_time, end_time: entry.end_time }
              : {}),
            notes: entry.notes || null,
          })),
        }),
      });
      setPendingSave(false);
      setDraft({});
      await Promise.all([loadAvailability(), loadSavedAvailability()]);
      toast.success('Disponibilità salvate');
    } catch (error) {
      toast.error(error.message || 'Non è stato possibile salvare le disponibilità.');
    } finally {
      setSaving(false);
    }
  };

  const confirmSingleReset = async () => {
    if (isSimulated) return;
    if (!pendingReset) return;
    setSaving(true);
    try {
      await apiFetch('/api/turni/my-availability', {
        method: 'PUT',
         body: JSON.stringify(singleAvailabilityResetPayload({
           shift_date: pendingReset.date,
           service_id: pendingReset.serviceId,
         })),
      });
      const resetKey = keyFor(pendingReset.date, pendingReset.serviceId);
      setDraft(current => {
        const next = { ...current };
        delete next[resetKey];
        return next;
      });
      setPendingReset(null);
      await Promise.all([loadAvailability(), loadSavedAvailability()]);
      toast.success('Disponibilità eliminata');
    } catch (error) {
      toast.error(error.message || 'Non è stato possibile eliminare la disponibilità.');
    } finally {
      setSaving(false);
    }
  };

  const openResetDialog = async () => {
    if (isSimulated) return;
    setResetDialog(true);
    setResetStep(1);
    setResetCount(null);
    setResetCountLoading(true);
    try {
      const result = await apiFetch('/api/turni/my-availability/count');
      setResetCount(Number(result.count) || 0);
    } catch (error) {
      setResetDialog(false);
      toast.error(error.message || 'Non è stato possibile contare le disponibilità future.');
    } finally {
      setResetCountLoading(false);
    }
  };

  const resetFuture = async () => {
    if (isSimulated) return;
    setSaving(true);
    try {
      await apiFetch('/api/turni/my-availability', { method: 'DELETE' });
      setResetDialog(false);
      setResetStep(1);
      setResetCount(null);
      setDraft({});
      await Promise.all([loadAvailability(), loadSavedAvailability()]);
      toast.success('Disponibilità future azzerate');
    } catch (error) {
      toast.error(error.message || 'Non è stato possibile azzerare le disponibilità.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-0px)] bg-app-background pb-[76px] md:pb-0" data-testid="my-shifts-page">
      <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/95 backdrop-blur-md md:hidden">
        <div className="flex min-h-[76px] items-center gap-3 px-4 py-3">
          <button type="button" onClick={openMobileMenu} className="flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 text-stone-600" aria-label="Apri menu"
            data-testid="my-shifts-mobile-menu-btn">
            <MenuIcon className="h-5 w-5" />
          </button>
          <img src="/brand/restaurantos-logo-symbol.svg" alt="" className="h-9 w-9" />
          <div className="min-w-0 flex-1 leading-none">
            <div className="mb-1 text-[11px] font-semibold tracking-tight text-stone-500">RestaurantOS</div>
            <div className="font-display text-[22px] font-bold tracking-tight text-brand-navy">I miei turni</div>
          </div>
          <NotificationsBell />
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-5 px-3 pb-5 pt-4 md:px-0 md:pt-0">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Area personale</div>
            <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight text-brand-navy md:text-4xl">
              <CalendarDays className="h-7 w-7 text-brand-green" /> I miei turni
            </h1>
            <p className="mt-1 text-sm text-stone-500">Consulta i turni pubblicati e comunica quando puoi lavorare.</p>
          </div>
        </div>

        {loadError && <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">{loadError}</div>}

        <section className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="my-shifts-published">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <h2 className="font-display text-lg font-semibold text-brand-navy">Turni pubblicati</h2>
              <p className="text-xs text-stone-500">Solo i turni confermati dal responsabile.</p>
            </div>
             <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-brand-green-hover">
               {publishedShiftCards.length}
             </span>
          </div>
           {isSimulated ? (
             <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50 px-4 py-7 text-center text-sm text-amber-900" data-testid="my-shifts-simulation-empty">
               La modalità simulazione non carica turni personali reali.
             </div>
           ) : loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-stone-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>
          ) : shifts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-7 text-center text-sm text-stone-500" data-testid="my-shifts-empty">
              Non ci sono turni pubblicati per le prossime settimane.
            </div>
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
               {publishedShiftCards.map(shift => {
                 const rest = isPublishedRest(shift);
                 const service = services.find(item => item.id === shift.service_id);
                return (
                   <div key={shift.shift_id || shift.id} className="flex items-center justify-between gap-3 rounded-xl border border-stone-100 bg-stone-50/70 px-3 py-3" data-testid={`my-shift-${shift.shift_id || shift.id}`}>
                    <div>
                      <div className="text-sm font-semibold capitalize text-brand-navy">{formatLongDate(localDate(shift.shift_date))}</div>
                       {rest ? (
                         <div className="mt-0.5 text-sm font-semibold text-emerald-800">Riposo · Giornata di riposo pubblicata</div>
                       ) : (
                         <div className="mt-0.5 text-xs text-stone-500">{service?.name || 'Servizio'} · {formatTime(shift.start_time)}–{formatTime(shift.end_time)}</div>
                       )}
                    </div>
                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700">{shift.status === 'CANCELLED' ? 'Annullato' : 'Pubblicato'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <Button type="button" onClick={() => setEditorOpen(current => !current)} disabled={loading || isSimulated}
          className="h-11 w-full rounded-xl bg-brand-green text-sm font-semibold text-white shadow-sm hover:bg-brand-green-hover md:w-auto md:self-start"
          data-testid="my-availability-open-editor">
          <FilePenLine className="mr-1.5 h-4 w-4" /> {editorOpen ? 'Chiudi disponibilità' : 'Imposta disponibilità'}
        </Button>

        <section className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 shadow-sm" data-testid="my-availability-saved-list">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <h2 className="font-display text-lg font-semibold text-brand-navy">Disponibilità future salvate</h2>
              <p className="text-xs text-stone-500">Puoi riaprire, modificare o eliminare ogni servizio separatamente.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-brand-green-hover">{futureAvailability.length}</span>
              {futureAvailability.length > 0 && (
                <Button type="button" variant="outline" onClick={openResetDialog} disabled={loading || saving || savedLoading}
                  className="h-9 rounded-xl border-stone-200 bg-white px-3 text-xs font-semibold text-stone-700"
                  data-testid="my-availability-reset-future">
                  <RotateCcw className="mr-1.5 h-4 w-4" /> Azzera future
                </Button>
              )}
            </div>
          </div>
          {savedLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-stone-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento disponibilità…</div>
          ) : futureAvailability.length === 0 ? (
            <div className="rounded-xl border border-dashed border-emerald-200 bg-white/70 px-4 py-6 text-center text-sm text-stone-500" data-testid="my-availability-saved-empty">
              Non hai ancora salvato disponibilità future.
            </div>
          ) : (
            <div className="space-y-2">
              {futureAvailability.map(item => (
                <SavedAvailabilityRow
                  key={item.id || `${item.shift_date}-${item.service_id || 'all'}`}
                  item={item}
                  service={services.find(service => service.id === item.service_id)}
                  onEdit={openSavedAvailability}
                  onDelete={itemToDelete => requestSingleReset(itemToDelete.shift_date, itemToDelete.service_id)}
                />
              ))}
            </div>
          )}
        </section>

        {historyAvailability.length > 0 && (
          <section className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="my-availability-history-list">
            <div className="mb-3">
              <h2 className="font-display text-lg font-semibold text-brand-navy">Storico disponibilità</h2>
              <p className="text-xs text-stone-500">Le disponibilità passate sono conservate e consultabili in sola lettura.</p>
            </div>
            <div className="space-y-2">
              {historyAvailability.map(item => (
                <SavedAvailabilityRow
                  key={item.id || `${item.shift_date}-${item.service_id || 'all'}`}
                  item={item}
                  service={services.find(service => service.id === item.service_id)}
                  readOnly
                />
              ))}
            </div>
          </section>
        )}

        {editorOpen && (
          <section className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-3 shadow-sm md:p-5" data-testid="my-availability-editor">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-xl font-semibold text-brand-navy">Imposta disponibilità</h2>
                  {dirty && <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold text-amber-800">Modifiche non salvate</span>}
                </div>
                <p className="mt-1 max-w-2xl text-sm text-stone-600">Compila separatamente Pranzo e Cena. Le date di oggi e passate sono di sola lettura.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={copyWeek} disabled={availabilityLoading || saving || !services.length}
                  className="h-9 rounded-xl border-stone-200 bg-white text-xs font-semibold" data-testid="my-availability-copy-week">
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copia settimana
                </Button>
                {dirty && <Button type="button" variant="outline" onClick={() => setDraft({})} disabled={saving}
                  className="h-9 rounded-xl border-stone-200 bg-white text-xs font-semibold" data-testid="my-availability-cancel">
                  Annulla
                </Button>}
                <Button type="button" onClick={() => saveAvailability(false)} disabled={!dirty || saving || availabilityLoading}
                  className="h-9 rounded-xl bg-brand-green text-xs font-semibold text-white hover:bg-brand-green-hover" data-testid="my-availability-save">
                  {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Salva
                </Button>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-white/80 bg-white/80 p-3 md:flex-row md:items-end md:justify-between">
              <div className="flex gap-1 rounded-xl bg-stone-100 p-1" role="tablist" aria-label="Periodo disponibilità">
                {['WEEK', 'CUSTOM'].map(item => (
                  <button type="button" key={item} role="tab" aria-selected={mode === item} onClick={() => changeMode(item)}
                    className={`rounded-lg px-3 py-2 text-xs font-bold ${mode === item ? 'bg-white text-brand-green shadow-sm' : 'text-stone-500'}`}
                    data-testid={`my-availability-mode-${item.toLowerCase()}`}>
                    {item === 'WEEK' ? 'Settimanale' : 'Periodo personalizzato'}
                  </button>
                ))}
              </div>
              {mode === 'WEEK' ? (
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => changeWeek(-1)} className="h-9 rounded-xl border border-stone-200 px-3 text-xs font-semibold text-stone-600 hover:bg-stone-50" data-testid="my-availability-prev-week">← Settimana precedente</button>
                  <span className="text-xs font-bold text-brand-navy">{formatShortDate(weekStart)}–{formatShortDate(addDays(weekStart, 6))}</span>
                  <button type="button" onClick={() => changeWeek(1)} className="h-9 rounded-xl border border-stone-200 px-3 text-xs font-semibold text-stone-600 hover:bg-stone-50" data-testid="my-availability-next-week">Settimana successiva →</button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[11px] font-bold text-stone-500">Dal<input type="date" min={localDateValue(addDays(new Date(), 1))} value={customStart} onChange={event => updateCustomRange(setCustomStart, event.target.value)} className="mt-1 h-9 rounded-lg border border-stone-200 px-2 text-xs font-normal text-stone-800" data-testid="my-availability-custom-start" /></label>
                  <label className="text-[11px] font-bold text-stone-500">Al<input type="date" min={customStart} value={customEnd} onChange={event => updateCustomRange(setCustomEnd, event.target.value)} className="mt-1 h-9 rounded-lg border border-stone-200 px-2 text-xs font-normal text-stone-800" data-testid="my-availability-custom-end" /></label>
                </div>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone-600" data-testid="my-availability-legend">
              <span className="font-bold uppercase tracking-wider text-stone-400">Stati</span>
              {AVAILABILITY_STATES.map(item => <span key={item.value} className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${item.value === 'DISPONIBILE' ? 'bg-emerald-500' : item.value === 'INDISPONIBILE' ? 'bg-red-500' : item.value === 'PREFERISCO_LAVORARE' ? 'bg-amber-500' : item.value === 'DISPONIBILE_SE_SERVE' ? 'bg-blue-500' : 'bg-stone-400'}`} />{item.label}</span>)}
            </div>

            {availabilityLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-stone-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento disponibilità…</div>
            ) : days.length > 31 ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Il periodo personalizzato non può superare 31 giorni.</div>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {days.map(day => <DayCard key={localDateValue(day)} date={day} services={services} getEntry={getEntry} onOpen={openDay} onCopy={copyDay} />)}
              </div>
            )}
            {dirty && <p className="mt-4 text-center text-xs font-medium text-stone-500">{Object.keys(draft).length} modifiche pronte da salvare.</p>}
          </section>
        )}
      </main>

      <Sheet open={!!selectedDay} onOpenChange={open => !open && setSelectedDay(null)}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl p-4 pb-8 sm:px-6" data-testid="my-availability-day-sheet">
          {selectedDay && (
            <>
              <SheetHeader className="mb-4 pr-8 text-left">
                <SheetTitle className="font-display text-xl text-brand-navy">{formatLongDate(selectedDay)}</SheetTitle>
                <SheetDescription>Imposta la disponibilità per Pranzo e Cena.</SheetDescription>
              </SheetHeader>
              <div className="space-y-3">
                {services.map(service => (
                  <ServiceAvailabilityForm key={service.id} service={service}
                    entry={getEntry(localDateValue(selectedDay), service.id)}
                     onChange={changes => updateEntry(localDateValue(selectedDay), service.id, changes)}
                     onReset={() => requestSingleReset(localDateValue(selectedDay), service.id)} />
                ))}
              </div>
              <Button type="button" onClick={() => setSelectedDay(null)} className="mt-4 h-11 w-full rounded-xl bg-brand-green font-semibold text-white hover:bg-brand-green-hover" data-testid="my-availability-day-done">Fine</Button>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!pendingReset} onOpenChange={open => !open && setPendingReset(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="my-availability-single-reset-dialog">
          <DialogHeader>
            <DialogTitle>Eliminare questa disponibilità?</DialogTitle>
            <DialogDescription>
              Verranno eliminati stato, fascia oraria e nota di {pendingReset && formatLongDate(localDate(pendingReset.date))}
              {' · '}
              {pendingReset && (services.find(service => service.id === pendingReset.serviceId)?.name || 'Tutti i servizi')}.
              Le altre date e l’altro servizio resteranno invariati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setPendingReset(null)} className="rounded-xl">Annulla</Button>
            <Button type="button" onClick={confirmSingleReset} disabled={saving}
              className="rounded-xl bg-red-600 text-white hover:bg-red-700" data-testid="my-availability-confirm-single-reset">
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingSave} onOpenChange={setPendingSave}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="my-availability-overwrite-dialog">
          <DialogHeader>
            <DialogTitle>Confermare la sovrascrittura?</DialogTitle>
            <DialogDescription>Una o più disponibilità esistenti verranno sostituite con i nuovi valori, inclusi eventuali orari e note.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setPendingSave(false)} className="rounded-xl">Annulla</Button>
            <Button type="button" onClick={() => saveAvailability(true)} className="rounded-xl bg-brand-green text-white hover:bg-brand-green-hover" data-testid="my-availability-confirm-overwrite">Sovrascrivi e salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetDialog} onOpenChange={open => {
        if (!open) {
          setResetDialog(false);
          setResetStep(1);
          setResetCount(null);
        }
      }}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="my-availability-reset-dialog">
          <DialogHeader>
            <DialogTitle>{resetStep === 1 ? 'Controlla il reset futuro' : 'Conferma definitiva'}</DialogTitle>
            <DialogDescription>
              {resetCountLoading
                ? 'Sto contando le disponibilità future del tuo profilo…'
                : `Saranno coinvolte esattamente ${resetCount || 0} disponibilità salvate da domani in avanti. Le disponibilità storiche non vengono modificate.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setResetDialog(false)} className="rounded-xl">Annulla</Button>
            {resetStep === 1 ? (
              <Button type="button" onClick={() => setResetStep(2)} disabled={resetCountLoading}
                className="rounded-xl bg-brand-green text-white hover:bg-brand-green-hover" data-testid="my-availability-reset-first-confirm">
                Continua
              </Button>
            ) : (
              <Button type="button" onClick={resetFuture} disabled={saving}
                className="rounded-xl bg-red-600 text-white hover:bg-red-700" data-testid="my-availability-confirm-reset">
                {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Conferma e azzera
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}